import { formatToDisplayDate } from '../utils/dateUtils'

export function formatMoney(value) {
    return `$${Number(value || 0).toLocaleString('es-AR')}`
}

// La API manda fechas "2026-04-30T00:00:00"; los DateInput trabajan con "2026-04-30"
export function toDateOnly(value) {
    return value ? value.slice(0, 10) : ''
}

// Fecha de hoy en hora local como "2026-04-30" (toISOString da el día UTC, que después de las 21 ya es mañana)
export function todayDateOnly() {
    const now = new Date()
    const pad = (n) => String(n).padStart(2, '0')
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

export function formatDate(value) {
    return formatToDisplayDate(toDateOnly(value))
}

export function formatDateTime(value) {
    if (!value) return ''
    const time = value.slice(11, 16)
    return time ? `${formatDate(value)} ${time}` : formatDate(value)
}

export function normalizePlanilla(planilla) {
    return {
        ...planilla,
        deliveryDate: toDateOnly(planilla.deliveryDate),
        loadDate: toDateOnly(planilla.loadDate),
    }
}

// La API guarda las patentes en mayúsculas y sin separadores (AF 151-MY → AF151MY)
export function normalizePlate(plate) {
    return (plate || '').toUpperCase().replace(/[^A-Z0-9]/g, '')
}

// El transporte se identifica por el código de GP; la descripción suele ser la dirección del depósito
export function transportLabel(transport) {
    return transport?.code ?? ''
}

export function plateNumbers(transport) {
    return (transport?.plates ?? []).map((p) => p.plate)
}

export const MAX_PLATE_LENGTH = 15

export const transportErrors = {
    NOT_FOUND: 500,
    RETIRA_CLIENTE: 502,
    FIELD_TOO_LONG: 503,
    INVALID_PLATE: 504,
    PLATE_NOT_FOUND: 505,
    INVALID_PLATE_FIELD: 506,
    INACTIVE: 606,
}

// Largos máximos de TransportSaveDTO y TransportPlateDTO
export const transportMaxLengths = {
    address: 61,
    city: 35,
    state: 29,
    country: 61,
    email: 256,
    notes: 500,
    brandModel: 64,
    driverDni: 15,
    driverName: 64,
}

export const PLATE_MAX_DECIMAL = 9999999999
export const PLATE_MAX_AXLES = 32767

export const isLogisticaOnly = (roles) =>
    !!roles?.includes('Logistica') && !roles.includes('Administrador')

// La API serializa en camelCase y puede mandar varios errores: { errors: [{ errorCode, errorDescription }] }
export function getApiErrors(err) {
    return err?.response?.data?.errors ?? []
}

export function getApiErrorCode(err) {
    return getApiErrors(err)[0]?.errorCode
}

export function getApiErrorMessage(err) {
    const errors = getApiErrors(err)
    return errors.length > 0 ? errors.map((e) => e.errorDescription).join(' ') : err.message
}

// Con responseType blob el error JSON también llega como Blob
export async function getBlobApiErrorMessage(err) {
    try {
        const data = JSON.parse(await err.response.data.text())
        return data?.errors?.map((e) => e.errorDescription).join(' ') || err.message
    } catch {
        return err.message
    }
}

export function downloadFile(blob, fileName) {
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    link.click()
    setTimeout(() => URL.revokeObjectURL(url), 0)
}

// La grilla mezcla facturas y transferencias de inventario; un guardado sin tipo la API lo toma como factura
export const documentTypes = {
    INVOICE: 'Invoice',
    TRANSFER: 'Transfer',
}

export const documentTypeLabels = {
    Invoice: 'Factura',
    Transfer: 'Transferencia',
}

export const documentTypeOf = (doc) => doc?.documentType || documentTypes.INVOICE

// Una línea se identifica por tipo + número: puede haber una factura y una transferencia con el mismo número
export const documentKey = (doc) => `${documentTypeOf(doc)}|${doc?.documentNumber}`

export function computeSeleccionTotales(documentos, selectedIds) {
    const seleccionados = documentos.filter((d) => selectedIds.has(documentKey(d)))
    return {
        netoTotal: seleccionados.reduce((acc, d) => acc + Number(d.netAmount || 0), 0),
        totalBultos: seleccionados.reduce((acc, d) => acc + Number(d.packages || 0), 0),
        documentosSeleccionados: seleccionados.length,
    }
}

// Bultos de la línea: entero, no negativo, sin tope
export const normalizeBultos = (value) => Math.max(0, parseInt(value, 10) || 0)

// Una línea sin bultos se marca en rojo y, tildada, no deja guardar la planilla
export const sinBultos = (doc) => normalizeBultos(doc?.packages) === 0

// Importe de la línea: cero o más, sin tope (un negativo la API lo rechaza con 604)
export const normalizeImporte = (value) => Math.max(0, Number(value) || 0)

// Nota de la línea: texto plano sin límite; vacía o solo espacios se guarda como nula
export const normalizeNote = (value) => (value?.trim() ? value : null)

export const hasNote = (doc) => !!doc?.note?.trim()

export const lineStatusLabels = {
    Active: 'Vigente',
    Replaced: 'Editada',
    Removed: 'Quitada',
    Cancelled: 'Anulada',
}
